import yaml from 'js-yaml'

/**
 * Overlay URL parameters onto an rjBuild's `data:` defaults.
 *
 * The contract is deliberately narrow and shared by every consumer (page
 * routes, the catch-all router, builder blocks): a param overrides a key ONLY
 * if that key is *already declared* in `data:`. A rjBuild therefore declares
 * which URL params it accepts simply by declaring their defaults — nothing to
 * register, nothing to whitelist elsewhere.
 *
 * MULTI-VALEUR : un défaut déclaré en TABLEAU (`provider: []`) marque une clé qui
 * accepte PLUSIEURS valeurs — la clé est alors répétée dans l'URL
 * (`?provider=anthropic&provider=openai`) et c'est la liste complète qui est
 * remontée (`getAll`). C'est ce qui permet à un filtre multi-select de voyager
 * jusqu'aux widgets (`ChartLoader` sait déjà émettre `filter[champ][]=` répétés
 * depuis un tableau). Un défaut scalaire garde l'ancien comportement : une seule
 * valeur, la dernière occurrence.
 *
 * @param {object} data            The rjBuild's own `data:` (not mutated).
 * @param {string} search          `location.search`.
 * @param {object} [options]
 * @param {string|number|null} [options.pathId]  Route path id, exposed as `id`
 *                                               unless `?id=` is already set.
 * @param {string[]} [options.exclude]           Param names to ignore (host
 *                                               chrome params, e.g. `edit`).
 * @returns {object|undefined} The merged object, or undefined if no param matched.
 */
/**
 * Une URL ne transporte que du TEXTE ; la déclaration, elle, dit le type.
 *
 * Les conditions de reactive-json comparent en `===` (cf. `engine/Actions.js`) :
 * un `trashPendingId: 0` déclaré par la page, surchargé à la chaîne `"1006"`,
 * ne pouvait plus égaler l'identifiant NUMÉRIQUE d'une ligne servie par
 * Elasticsearch — la condition rendait faux, en silence, et l'écran n'en disait
 * rien. On rend donc au paramètre le type de la valeur qu'il remplace.
 *
 * Prudent par construction : seule une clé déclarée en NOMBRE est convertie, et
 * seulement si le texte reçu en est vraiment un (« abc » sur une clé numérique
 * reste « abc » plutôt que de devenir NaN, qui n'égale même pas lui-même).
 */
const coerceToDeclaredType = (declared, raw) => {
  if (typeof declared !== 'number' || raw === '') return raw
  const n = Number(raw)
  return Number.isFinite(n) ? n : raw
}

export const overlayUrlParams = (data, search, { pathId = null, exclude = [] } = {}) => {
  if (!data || typeof data !== 'object') return undefined

  const params = new URLSearchParams(search)
  if (pathId != null && !params.has('id')) params.set('id', String(pathId))

  const merged = { ...data }
  let changed = false
  // On itère les clés DÉCLARÉES (et non les paramètres) : c'est la déclaration
  // qui dit si la clé attend une valeur ou une liste. Le contrat est inchangé —
  // un paramètre ne surcharge que ce que le rjBuild déclare déjà.
  for (const key of Object.keys(merged)) {
    if (exclude.includes(key) || !params.has(key)) continue
    merged[key] = Array.isArray(merged[key])
      ? params.getAll(key)
      : coerceToDeclaredType(merged[key], params.get(key))
    changed = true
  }
  return changed ? merged : undefined
}

/**
 * Seed the reactive-json store from the page URL — the native alternative to
 * baking `__URL_*__` tokens into the YAML text.
 *
 * Every URL query param (plus the route's path id, exposed as `id`) that
 * *matches a top-level key already declared in the page's `data:`* overrides
 * that key's default. The merged object is meant to be handed to
 * `ReactiveJsonRoot` as `dataOverride`, which **replaces** the data root — so we
 * overlay the URL values onto the YAML's own `data:` to preserve everything else
 * (`ReactiveJsonRoot.js`: `c = dataOverride !== undefined ? dataOverride : r.data`).
 *
 * Additive & safe: a page that declares no matching key gets `undefined`
 * (no override → identical behaviour). The YAML then reads `~~.wsid` etc. and
 * forwards it natively (additionalDataSource `{param, value}` / submitData body),
 * instead of relying on text substitution.
 *
 * @param {string} content  The (already token-substituted) rjBuild text.
 * @param {string} search   `location.search`.
 * @param {string|number|null} pathId  Optional route path id (`/x/:id`).
 * @returns {object|undefined} The merged data object, or undefined if nothing matched.
 */
export const buildDataOverride = (content, search, pathId) => {
  let parsed
  try { parsed = yaml.load(content) } catch { return undefined }
  if (!parsed || typeof parsed.data !== 'object' || parsed.data === null) return undefined

  return overlayUrlParams(parsed.data, search, { pathId })
}
